// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, get, getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import Search from "@west/XDL/Icons/Search";
import { fontConfig } from "@west/XDL/PreStyle/typography";
import { color, mergeWithCss, styledScrollbar } from "@west/XDL/Utilities";
import styled, { css } from "styled-components";

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        background-color: ${resolveColor("common.white", theme)};
        box-shadow: ${({ theme }) => get(theme, "shadows.2")};
        border: 0.8px solid ${resolveColor("common.ui3", theme)};
        border-radius: 4px;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;

        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 12px;
                padding: 10px 15px;
                color: ${resolveColor("text", theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor("common.ui2", theme)};
                color: ${resolveColor("text", theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [20] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const createLinkIconStyles = (
    icon: React.ComponentType | string,
    linkStyles?: LinkPresentationProps,
    typographyStyles?: TypographyPresentationProps,
    iconStyles?: StyledProp,
    iconPosition: "left" | "right" = "right",
) => {
    const marginPosition = (linkStyles?.icon?.position || iconPosition) === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: 12,
                    lineHeight: "13px",
                    weight: fontConfig.weight.bold,
                    css: css`
                        letter-spacing: 0;
                    `,
                    typographyStyles,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: 12,
                        css: css`
                            ${marginPosition}: 8px;
                            ${iconStyles}
                        `,
                    },
                    position: linkStyles?.icon?.position || iconPosition,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const styledBoxStyle = css`
    width: 100%;
    display: flex;
    padding: 13px;
    border: 1px solid ${color("common.border")};
    background-color: ${color("common.white")};
`;

export const searchfieldInputStyle: TextFieldProps = {
    mergeCss: true,
    iconProps: {
        size: 15,
        src: Search,
        color: "common.ui8",
    },
    cssOverrides: {
        formField: css`
            & ${IconWrapper} {
                svg {
                    height: 15px;
                    width: 15px;
                }
            }
            &:hover {
                & ${IconWrapper} {
                    color: ${color("common.ui9")};
                    svg {
                        color: ${color("common.ui9")};
                    }
                }
            }
        `,
    },
};

// This Wrapper used For total display ( order total, cart total, ...)
export const StyledBox = styled(StyledWrapper)<{ borderRadius?: number }>`
    ${styledBoxStyle}
    ${({ borderRadius }) =>
        borderRadius &&
        css`
            border-radius: ${borderRadius}px;
        `}

    @media print {
        max-width: 400px;
    }
    ${avoidBreakInsidePrintElement}

    ${injectCss}
`;

export const CenteringWrapper = styled(StyledWrapper)`
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${LoadingSpinnerStyle} {
        margin: auto;
    }
`;
